import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

export const _frontmatter = {
  "title": "How to setup Gocommerce on a new Digital Ocean droplet",
  "description": "",
  "published": "2018-01-23T00:00:00.000Z",
  "redirect_from": ["/blog/setup-gocommerce-new-digital-ocean-droplet/swizec/8152"],
  "categories": "Go, Linux, Technical, Ubuntu",
  "hero": "../../../defaultHero.jpg"
};
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <p><a parentName="p" {...{
        "href": "https://www.youtube.com/embed/O0P7YIiQm4Y?start=11147"
      }}><img parentName="a" {...{
          "src": "https://swizec.com/blog/wp-content/uploads/2018/01/Screen-Shot-on-2018-01-23-at-141356-1024x577.png",
          "alt": null
        }}></img></a></p>
    <p><a parentName="p" {...{
        "href": "https://github.com/netlify/gocommerce"
      }}>{`GoCommerce`}</a>{` is Netlify's headless e-commerce for JAMstack sites. Whatever that means.`}</p>
    <p>{`It acts as your database when you have a single page app (possibly in React) that sells stuff. Integrates through `}<inlineCode parentName="p">{`<script>`}</inlineCode>{` tags with JSON in them.`}</p>
    <p>{`I hear Netlify is working on a fully hosted solution. It's not ready yet.`}</p>
    <p><a parentName="p" {...{
        "href": "https://www.digitalocean.com/"
      }}>{`Digital Ocean`}</a>{` droplets are a glorified VPS, virtual private server. I'm told it's actually an IaaS (infrastructure as a service) and competes with Amazon's AWS…`}</p>
    <p>{`Sure felt a lot like ssh-ing into a VPS and mucking about. 🤷‍♀️`}</p>
    <p>{`Recently, I had to make these two work together. I looked for a guide, but none could be found. So here's a collection of my notes with some explanation.`}</p>
    <p>{`The whole process takes about 10 minutes, if you know what you're doing. Writing this will help me know what I'm doing next time :)`}</p>
    <h2 {...{
      "id": "get-a-droplet-then-set-it-up"
    }}>{`Get a droplet, then set it up`}</h2>
    <p>{`I was given an existing droplet. This assumes you have a Digital Ocean Droplet running Ubuntu. Just the initial `}<em parentName="p">{`"Here's your VPS"`}</em>{` setup that comes out of the box.`}</p>
    <p>{`👉 `}<a parentName="p" {...{
        "href": "https://www.digitalocean.com/community/tutorials/how-to-launch-your-site-on-a-new-ubuntu-14-04-server-with-lamp-sftp-and-dns"
      }}>{`First step of this tutorial`}</a></p>
    <p>{`Next we have to set up a user because running things as `}<inlineCode parentName="p">{`root`}</inlineCode>{` is bad. I won't go into why it's bad right now because it's just something I remember from my Linux-using days many years ago. It's bad. Don't do it.`}</p>
    <p>{`👉 `}<a parentName="p" {...{
        "href": "https://www.digitalocean.com/community/tutorials/initial-server-setup-with-ubuntu-14-04"
      }}>{`Follow this tutorial`}</a></p>
    <p>{`The instructions boil down to:`}</p>
    <ol>
      <li parentName="ol">{`Login as root`}</li>
      <li parentName="ol">{`Create new user (I used `}<inlineCode parentName="li">{`gocommerce`}</inlineCode>{`)`}</li>
      <li parentName="ol">{`Give it `}<inlineCode parentName="li">{`sudo`}</inlineCode>{` privileges`}</li>
      <li parentName="ol">{`Add your personal public ssh key to the server's authorized keys`}</li>
      <li parentName="ol">{`Login as new user`}</li>
    </ol>
    <h2 {...{
      "id": "login-install-sqlite"
    }}>{`Login, install SQLite`}</h2>
    <p>{`Once you have a new user, it's time to login and set up Gocommerce. This involves installing `}<inlineCode parentName="p">{`golang`}</inlineCode>{`, `}<inlineCode parentName="p">{`sqlite3`}</inlineCode>{`, and a few knickknacks.`}</p>
    <p>{`Login to your server. My user was called `}<inlineCode parentName="p">{`gocommerce`}</inlineCode>{`.`}</p>
    <pre><code parentName="pre" {...{}}>{`$ ssh gocommerce@<YOUR_IP>
`}</code></pre>
    <p>{`Then install `}<inlineCode parentName="p">{`sqlite3`}</inlineCode>{`. `}<a parentName="p" {...{
        "href": "https://sqlite.org/"
      }}>{`SQLite`}</a>{` is a simple database that's easy to setup. If you want to use this for real production, I suggest opting for Postgres.`}</p>
    <p>{`👉 `}<a parentName="p" {...{
        "href": "https://www.quora.com/How-can-I-install-SQLite-in-Ubuntu-14-04-2-LTS"
      }}>{`Following this Quora answer`}</a></p>
    <pre><code parentName="pre" {...{}}>{`$ sudo apt-get update
$ sudo apt-get install sqlite3 sqlite3-dev
`}</code></pre>
    <p><inlineCode parentName="p">{`apt`}</inlineCode>{` is Ubuntu's package management system. Much like `}<inlineCode parentName="p">{`npm`}</inlineCode>{` for JavaScript. `}<inlineCode parentName="p">{`update`}</inlineCode>{` updates the local listing of software packages, `}<inlineCode parentName="p">{`install`}</inlineCode>{` installs them. I used to know why this was `}<inlineCode parentName="p">{`apt-get`}</inlineCode>{` and not just `}<inlineCode parentName="p">{`apt`}</inlineCode>{`.`}</p>
    <p>{`I also used to know why you need to install `}<inlineCode parentName="p">{`sqlite3-dev`}</inlineCode>{`, but I have since forgotten. Doesn't matter.`}</p>
    <p>{`You now have SQLite and can create local database. Single file, SQL interface. Wonderful.`}</p>
    <h2 {...{
      "id": "install-go"
    }}>{`Install Go`}</h2>
    <p>{`Installing `}<a parentName="p" {...{
        "href": "https://golang.org/"
      }}>{`Golang`}</a>{` is a little harder. You can't use the default package because Gocommerce needs at least Go version 1.7.`}</p>
    <p>{`As I painfully discovered, the official version in Ubuntu packages is 1.6, and that's not good enough.`}</p>
    <p>{`👉 `}<a parentName="p" {...{
        "href": "https://github.com/golang/go/wiki/Ubuntu"
      }}>{`Followed this guide to install`}</a></p>
    <p>{`👉 `}<a parentName="p" {...{
        "href": "https://medium.com/@patdhlk/how-to-install-go-1-9-1-on-ubuntu-16-04-ee64c073cd79"
      }}>{`And this guide to configure`}</a></p>
    <p>{`Here's what it boils down to.`}</p>
    <p>{`You install Go with these `}<inlineCode parentName="p">{`apt-get`}</inlineCode>{` incantations.`}</p>
    <pre><code parentName="pre" {...{}}>{`$ sudo add-apt-repository ppa:gophers/archive
$ sudo apt-get update
$ sudo apt-get install golang-1.9-go
`}</code></pre>
    <p>{`I installed the incorrect version of Go at first, so I also had to overwrite its executable with a link to the new one. You might not have to do this, and it's probably a bad way of doing it, but 👇`}</p>
    <pre><code parentName="pre" {...{}}>{`$ cd /usr/bin
$ sudo rm go
$ sudo ln -s /usr/lib/go-1.9/bin/go
`}</code></pre>
    <p>{`When you run `}<inlineCode parentName="p">{`go version`}</inlineCode>{` it should say 1.9.`}</p>
    <p>{`Then we get to create `}<inlineCode parentName="p">{`$GOPATH`}</inlineCode>{` and set it up in our `}<inlineCode parentName="p">{`.bashrc`}</inlineCode>{`.`}</p>
    <pre><code parentName="pre" {...{}}>{`$ vim ~/.bashrc
`}</code></pre>
    <p>{`Add these lines at the end of that file:`}</p>
    <pre><code parentName="pre" {...{}}>{`export GOPATH="$HOME/go"
export GOBIN="$GOPATH/bin"
export PATH="$GOBIN:$PATH"
export PATH="$PATH:/usr/local/go/bin"
`}</code></pre>
    <p>{`This creates your `}<inlineCode parentName="p">{`$GOPATH`}</inlineCode>{` and `}<inlineCode parentName="p">{`$GOBIN`}</inlineCode>{` variables then adds them to `}<inlineCode parentName="p">{`$PATH`}</inlineCode>{`. Go needs these to be able to execute stuff and load itself up.`}</p>
    <p>{`Next you have to create that directory →`}</p>
    <pre><code parentName="pre" {...{}}>{`$ mkdir -p ~/go/src
`}</code></pre>
    <p>{`This puts a directory called `}<inlineCode parentName="p">{`go`}</inlineCode>{` in your home dir and a `}<inlineCode parentName="p">{`src`}</inlineCode>{` dir inside that. `}<inlineCode parentName="p">{`~/go/src`}</inlineCode>{` is where all your Go code is going to live.`}</p>
    <h2 {...{
      "id": "build-configure-and-run-gocommerce"
    }}>{`Build, configure, and run Gocommerce`}</h2>
    <p>{`Now that you have Go running, it's time to set up Gocommerce.`}</p>
    <p>{`First, create the directory it's going to live in.`}</p>
    <pre><code parentName="pre" {...{}}>{`$ mkdir ~/go/src/github.com/netlify
`}</code></pre>
    <p>{`Next go there and clone the repo from Github. Ubuntu comes with git preinstalled, so there's no need to worry about that.`}</p>
    <pre><code parentName="pre" {...{}}>{`$ cd ~/go/src/github.com/netlify
$ make deps
$ make build_linux
`}</code></pre>
    <p><inlineCode parentName="p">{`make deps`}</inlineCode>{` installs Go dependencies that Gocommerce uses and `}<inlineCode parentName="p">{`make build_linux`}</inlineCode>{` builds Gocommerce itself. You have to build it for Linux because we're running Ubuntu.`}</p>
    <p>{`On your dev machine, if it's a Mac, you'd use `}<inlineCode parentName="p">{`make build`}</inlineCode>{` to compile for a Mac. You can see what Netlify considers the default :)`}</p>
    <h3 {...{
      "id": "configure"
    }}>{`Configure`}</h3>
    <p>{`To configure Gocommerce, you edit a `}<inlineCode parentName="p">{`.env`}</inlineCode>{` file in its dir, `}<inlineCode parentName="p">{`~/go/src/github.com/netlify/gocommerce/.env`}</inlineCode></p>
    <p>{`You can see details in `}<a parentName="p" {...{
        "href": "https://github.com/netlify/gocommerce"
      }}>{`Gocommerce README`}</a>{`. Something like this 👇`}</p>
    <pre><code parentName="pre" {...{}}>{`GOCOMMERCE_SITE_URL=http://jamcommerce.netlify.com
GOCOMMERCE_JWT_SECRET="thisissupersecret"
GOCOMMERCE_DB_DRIVER=sqlite3
DATABASE_URL=gotrue.db
GOCOMMERCE_DB_AUTOMIGRATE=true
GOCOMMERCE_API_HOST=<your ip>
PORT=9111
GOCOMMERCE_MAILER_HOST=smtp.sendgrid.net
GOCOMMERCE_MAILER_PORT=587
GOCOMMERCE_MAILER_USER=swizec+jamcommerce@swizec.com
GOCOMMERCE_MAILER_PASS=<mailer pass>
GOCOMMERCE_MAILER_SUBJECTS_ORDER_CONFIRMATION="Thank you for your order!"
GOCOMMERCE_MAILER_SUBJECTS_ORDER_RECEIVED="A new order has been placed"
GOCOMMERCE_PAYMENT_STRIPE_ENABLED=true
GOCOMMERCE_PAYMENT_STRIPE_SECRET_KEY=<your stripe key>
`}</code></pre>
    <p>{`The tricky one to find was that `}<inlineCode parentName="p">{`GOCOMMERCE_DB_AUTOMIGRATE`}</inlineCode>{`. That keeps your database schema up to date.`}</p>
    <p>{`You'll need Stripe (or Paypal) and some sort of SMTP service. I'm using sendgrid here.`}</p>
    <h3 {...{
      "id": "run"
    }}>{`Run`}</h3>
    <p>{`You can run Gocommerce using `}<inlineCode parentName="p">{`./gocommerce`}</inlineCode>{`. This runs the service directly, and you can check that it's running at `}<inlineCode parentName="p">{`http://<your ip>:9111`}</inlineCode>{`.`}</p>
    <p>{`But it's going to stop running once your ssh session times out. Not good.`}</p>
    <p>{`We have to turn `}<inlineCode parentName="p">{`gocommerce`}</inlineCode>{` into a Daemon, a script that runs in the background.`}</p>
    <p>{`👉 `}<a parentName="p" {...{
        "href": "https://www.devdungeon.com/content/creating-systemd-service-files"
      }}>{`Follow this guide for creating a custom systemd service`}</a></p>
    <p>{`systemd is the system that Ubuntu uses to manage automatically starting scripts.`}</p>
    <p>{`To do this, you create `}<inlineCode parentName="p">{`/etc/systemd/system/gocommerce.service`}</inlineCode>{`. I used vim, so `}<inlineCode parentName="p">{`sudo vim /etc/systemd/system/gocommerce.service`}</inlineCode>{`.`}</p>
    <pre><code parentName="pre" {...{}}>{`[Unit]
Description=Gocommerce Service
After=network.target

[Service]
Type=simple
User=gocommerce
WorkingDirectory=/home/gocommerce/go/src/github.com/netlify/gocommerce
ExecStart=/home/gocommerce/go/src/github.com/netlify/gocommerce/gocommerce
Restart=on-abort

[Install]
WantedBy=multi-user.targer
`}</code></pre>
    <p>{`This assumes the user you created earlier is called `}<inlineCode parentName="p">{`gocommerce`}</inlineCode>{` and that you haven't moved the `}<inlineCode parentName="p">{`gocommerce`}</inlineCode>{` executable.`}</p>
    <p>{`You can now start the service.`}</p>
    <pre><code parentName="pre" {...{}}>{`$ systemctl start gocommerce
`}</code></pre>
    <p>{`You should see a 404 error page on `}<inlineCode parentName="p">{`http://<your ip>:9111`}</inlineCode>{`. That means gocommerce is running and serving request.`}</p>
    <h2 {...{
      "id": ""
    }}>{`👌`}</h2>
    <p>{`You can now close your ssh connection and `}<inlineCode parentName="p">{`gocommerce`}</inlineCode>{` should keep running in the background forever.`}</p>
    <p>{`Congratz.`}</p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      